import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Chip, Typography, debounce } from '@mui/material';

import { gql } from '@graphql/__generated__';
import {
  type GetLeadDataQuery,
  type LeadAgentFragment,
} from '@graphql/__generated__/graphql';
import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { WarningAlert } from '@modules/_shared/components/WarningAlert';
import { useUpdateLeadAgents } from '@modules/_shared/hooks/useUpdateLeadAgents';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { GET_LEAD_AGENTS } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';

type LeadDetailsQualificationCardProps = {
  leadId: string;
  selectedAgentId?: string | null;
  setSelectedAgentId?: (agentId: string | null) => void;
};

type LeadTagOption = GetLeadDataQuery['dictionaries'][number];

type QualificationFormValues = {
  agent_id?: string | null;
  win_probability?: number | null;
  predicted_win_date?: string | null;
  tags?: LeadTagOption[] | null;
};

const GET_LEAD_DATA = gql(/* GraphQL */ `
  query GetLeadData($lead_id: uuid!) {
    leads_by_pk(id: $lead_id) {
      id
      lots {
        id
      }
      leads_tags {
        id
        created_by
        created_at
        dictionary {
          id
          label
        }
      }
    }
    dictionaries(where: { type: { _eq: tags_types } }) {
      id
      label
    }
  }
`);

const UPDATE_LEAD_TAGS = gql(/* GraphQL */ `
  mutation UpdateLeadTags(
    $removedTags: [uuid!]!
    $newTags: [leads_tags_insert_input!]!
  ) {
    delete_leads_tags(where: { id: { _in: $removedTags } }) {
      affected_rows
    }
    insert_leads_tags(objects: $newTags) {
      affected_rows
    }
  }
`);

export const LeadDetailsQualificationCard = ({
  leadId,
  selectedAgentId,
  setSelectedAgentId,
}: LeadDetailsQualificationCardProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const isAdmin = me?.is_admin;

  const { data, loading } = useQuery(GET_LEAD_AGENTS, {
    variables: { lead_id: leadId },
  });

  const { data: leadData } = useQuery(GET_LEAD_DATA, {
    variables: { lead_id: leadId },
  });

  const [updateLeadAgent] = useUpdateLeadAgents({
    onError: setErrorLike,
  });
  const [updateLeadTags] = useMutation(UPDATE_LEAD_TAGS, {
    refetchQueries: [GET_LEAD_DATA],
    onError: setErrorLike,
  });

  // Debounce updates to avoid rapid successive mutations
  const debouncedUpdateLeadAgent = useMemo(
    () => debounce(updateLeadAgent, 3000),
    [updateLeadAgent],
  );

  const uniqueAgents = useMemo(() => {
    if (data == null) {
      return [];
    }

    // Create a map to dedupe lead_agents by unique user IDs
    const map = new Map<string, LeadAgentFragment>(
      [...data.lead_agents]
        .sort((a, b) => {
          // Prioritize non-crm sources so they appear first in the array and CRM source override the items of the map
          if (a.source !== 'crm' && b.source === 'crm') {
            return -1;
          }
          if (a.source === 'crm' && b.source !== 'crm') {
            return 1;
          }
          return 0;
        })
        .map(agent => [agent.user.id, agent]),
    );

    return Array.from(map.values());
  }, [data]);

  const shouldDefaultExpandQualification = useMemo(() => {
    const hasTags = (leadData?.leads_by_pk?.leads_tags?.length ?? 0) > 0;
    const hasWinProbability = uniqueAgents.some(
      agent => agent.win_probability != null,
    );
    const hasPredictedWinDate = uniqueAgents.some(
      agent => agent.predicted_win_date != null,
    );

    return hasTags || hasWinProbability || hasPredictedWinDate;
  }, [leadData?.leads_by_pk?.leads_tags, uniqueAgents]);

  // Get the current lead agent
  const selectedLeadAgent = isAdmin
    ? // Admins can select any agent (first one is automatically selected if only one agent is assigned)
      uniqueAgents.length === 1
      ? uniqueAgents[0]
      : uniqueAgents.find(agent => agent.user.id === selectedAgentId)
    : // Non-admins always use the first visible agent; updates fan out to the team
      uniqueAgents.find(a => a.user.id === me?.id) ?? uniqueAgents[0] ?? null;

  const defaultValues = useMemo(
    () => ({
      agent_id: selectedAgentId ?? null,
      win_probability: selectedLeadAgent?.win_probability ?? null,
      predicted_win_date: selectedLeadAgent?.predicted_win_date ?? null,
      tags:
        leadData?.leads_by_pk?.leads_tags
          ?.map(tag => tag.dictionary)
          ?.filter(
            (dictionary): dictionary is LeadTagOption => dictionary != null,
          ) ?? [],
    }),
    [selectedAgentId, selectedLeadAgent, leadData?.leads_by_pk?.leads_tags],
  );

  const formDefinition: FormDefinitionType<QualificationFormValues> = useMemo(
    () => () => {
      return [
        {
          type: 'tags',
          name: 'tags',
          label: t('Tags'),
          gridProps: { md: 12 },
          onFilter: async (inputStr: string) => {
            return (leadData?.dictionaries ?? []).filter(
              option =>
                option.label
                  ?.toLowerCase?.()
                  ?.includes?.(inputStr.toLowerCase()) ?? false,
            );
          },
          renderOption(props, option) {
            return <li {...props}>{option.label}</li>;
          },
          getOptionLabel: option => option.label,
          renderTags: (
            value: { label: string }[],
            getTagProps: (params: { index: number }) => {
              key: number | string;
              [k: string]: unknown;
            },
          ) => {
            return value.map((option: { label: string }, index: number) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} label={option.label} {...tagProps} />;
            });
          },
          render: () => isAdmin ?? false,
        },
        {
          type: 'select',
          name: 'agent_id',
          label: t('Agent'),
          gridProps: { md: 12 },
          options: () =>
            uniqueAgents.map(agent => ({
              value: agent.user.id,
              label: `${agent.user.first_name} ${agent.user.last_name}`,
            })),
          render: () => !!isAdmin && uniqueAgents.length > 1,
        },
        {
          type: 'select',
          name: 'win_probability',
          label: t('Win Probability'),
          gridProps: { md: 6 },
          // Generate options from 5 to 100 in steps of 5
          options: () =>
            Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map(value => ({
              value,
              label: `${value}%`,
            })),
          renderValue: (value: any) => (value != null ? `${value}%` : ''),
          render: () => !!selectedLeadAgent,
        },
        {
          type: 'date',
          name: 'predicted_win_date',
          label: t('Predicted Win Date'),
          gridProps: { md: 6 },
          render: () => !!selectedLeadAgent,
        },
      ];
    },
    [isAdmin, leadData?.dictionaries, t, selectedLeadAgent, uniqueAgents],
  );

  const handleFormChange = useCallback<RaFormOnChange<QualificationFormValues>>(
    async (formData, field, type) => {
      if (type !== 'change') {
        return;
      }

      if (field === 'agent_id') {
        setSelectedAgentId?.(formData.agent_id ?? null);
        return;
      }

      if (field === 'tags') {
        // Diff the tags from the lead and the new tags
        const newTags = formData.tags?.filter(
          tag =>
            !leadData?.leads_by_pk?.leads_tags.some(
              t => t.dictionary?.id === tag?.id,
            ),
        );
        const removedTags = leadData?.leads_by_pk?.leads_tags.filter(
          tag => !formData.tags?.some(t => t?.id === tag.dictionary?.id),
        );
        const hasNewTags = (newTags?.length ?? 0) > 0;
        const hasRemovedTags = (removedTags?.length ?? 0) > 0;
        if (hasNewTags || hasRemovedTags) {
          await updateLeadTags({
            variables: {
              newTags:
                newTags?.map(tag => ({
                  lead_id: leadId,
                  dictionary_id: tag?.id,
                })) ?? [],
              removedTags: removedTags?.map(tag => tag.id) ?? [],
            },
          });
        }
        return;
      }

      if (field == null || selectedLeadAgent?.user.id == null) {
        return;
      }

      if (field === 'predicted_win_date') {
        // use debounce, do not update the date until the user has stopped typing
        return debouncedUpdateLeadAgent(
          leadId,
          { predicted_win_date: formData.predicted_win_date },
          selectedLeadAgent.user.id,
        );
      }

      await updateLeadAgent(
        leadId,
        { [field]: formData[field as keyof QualificationFormValues] },
        selectedLeadAgent.user.id,
      );
    },
    [
      debouncedUpdateLeadAgent,
      leadData?.leads_by_pk?.leads_tags,
      leadId,
      selectedLeadAgent?.user.id,
      setSelectedAgentId,
      updateLeadAgent,
      updateLeadTags,
    ],
  );

  if (loading) {
    return null;
  }

  return (
    <ListingAccordion
      title={t('Qualification')}
      defaultExpanded={shouldDefaultExpandQualification}
      loading={loading}
    >
      <RaForm
        formDefinition={formDefinition}
        defaultValues={defaultValues}
        onChange={handleFormChange}
        gridProps={{
          sx: { p: 0 },
        }}
        actionButtonsComponent={<></>}
      />
      {data != null && data.lead_agents.length === 0 && (
        <WarningAlert
          title={t('No agents found for this lead.')}
          sx={{ mt: 2 }}
        >
          <Typography variant="body2">
            {t('Please assign an agent to start qualifying.')}
          </Typography>
        </WarningAlert>
      )}
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </ListingAccordion>
  );
};
